import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Pencil, Plus, Grid, List } from "lucide-react"
import { listDecks, getTokenBalance } from "@/services/api"
import { LoadingSpinner } from "@/components/ui/loading-spinner"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { DeckCard } from "@/components/decks/DeckCard"

interface Deck {
  id: number;
  name: string;
  tagline: string;
  universe_name: string;
  card_count: number;
  is_public: boolean;
  is_collaborative: boolean;
  created_at: string;
  metadata?: any;
  user_id?: number;
}

export function DeckList() {
  const [decks, setDecks] = useState<Deck[]>([])
  const [loading, setLoading] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const navigate = useNavigate()

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const response = await getTokenBalance();
        setIsAdmin(response.data.data.tier === 'admin');
      } catch (error) {
        console.error('Failed to check admin status:', error);
        setIsAdmin(false);
      }
    };

    checkAdminStatus();
  }, []);

  useEffect(() => {
    const fetchDecks = async () => {
      try {
        setLoading(true)
        const response = await listDecks()
        console.log('Decks API Response:', response)
        console.log('Auth Headers:', response.config.headers)
        
        const decksData = Array.isArray(response?.data?.data) 
          ? response.data.data 
          : [];
        
        console.log('Processed Decks Data:', decksData)
        setDecks(decksData)
      } catch (error) {
        if (error && typeof error === 'object' && 'response' in error) {
          const err = error as { response: { data: any; status: number } };
          console.error('Error Response:', err.response.data);
          console.error('Error Status:', err.response.status);
        } else {
          console.error('Error:', error);
        }
        setDecks([])
      } finally {
        setLoading(false)
      }
    }

    fetchDecks()
  }, [showAll, isAdmin])

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen md:ml-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="w-full h-full">
      <div className="w-full max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold"></h1>
          <div className="flex items-center gap-4">
            {isAdmin && (
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-all"
                  checked={showAll}
                  onCheckedChange={setShowAll}
                />
                <Label htmlFor="show-all">Show All Decks</Label>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'bg-accent' : ''}
              >
                <Grid className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-accent' : ''}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="rounded-md border bg-card w-full">
          {decks.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <p className="text-muted-foreground mb-4">No decks found</p>
              <Button onClick={() => navigate('/decks/create')}>
                <Plus className="mr-2 h-4 w-4" /> Create Your First Deck
              </Button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {decks.map((deck) => (
                <DeckCard 
                  key={deck.id} 
                  deck={deck} 
                  onEdit={() => navigate(`/decks/${deck.id}`)}
                />
              ))}
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-12rem)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Name</TableHead>
                    <TableHead className="hidden md:table-cell">Tagline</TableHead>
                    {/* <TableHead className="w-[100px]">Cards</TableHead> */}
                    <TableHead className="w-[100px]">Visibility</TableHead>
                    <TableHead className="w-[100px]">Collab</TableHead>
                    <TableHead className="hidden lg:table-cell">Created At</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {decks.map((deck) => (
                    <TableRow key={deck.id}>
                      <TableCell className="font-medium">{deck.name}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {deck.tagline || 'No tagline available'}
                      </TableCell>
                      {/* <TableCell>{deck.card_count}</TableCell> */}
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded ${
                          Number(deck.is_public) === 1
                            ? 'bg-green-500/10 text-green-500' 
                            : 'bg-blue-500/10 text-blue-500'
                        }`}>
                          {Number(deck.is_public) === 1 ? 'Public' : 'Private'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs px-2 py-1 rounded ${
                          Number(deck.is_collaborative) === 1
                            ? 'bg-purple-500/10 text-purple-500' 
                            : 'bg-slate-500/10 text-slate-500'
                        }`}>
                          {Number(deck.is_collaborative) === 1 ? 'Collab' : 'Solo'}
                        </span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {new Date(deck.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/decks/${deck.id}`)}
                          className="p-3 sm:p-2"
                        >
                          <Pencil className="h-6 w-6 sm:h-4 sm:w-4 mr-2" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </div>
      </div>
    </div>
  )
} 